import {
  type AsyncResult,
  SynchrounousState,
  type VoidAsyncResult,
  asVoidAsyncResult,
  createResolvedAsyncResult,
} from "@/cache/AsyncResult";
import { PoiParameterRequestKind } from "@/cache/GeoJSONCache";
import { networkCaches } from "@/cache/GlobalCache";
import { emptyFeatureCollection, emptyGeoJsonSource } from "@/geojson";
import { ChangeDetector } from "@/layers/ChangeDetector";
import type { ScenePublicApi } from "@/layers/Compositor";
import { type CheckedProps, LayerBase, type PropsChecker } from "@/layers/LayerBase";
import { generatePOILayerCacheId } from "@/layers/geojson/LayerUtils";
import { loadTextBackgroundImage } from "@/layers/geojson/poi/PoiLayerUtilities/poi-mapbox-utils";
import { partitionPoiCoordsByBoundary } from "@/layers/geojson/poi/poiBoundary";
import { handleRenderTooltip } from "@/layers/geojson/poi/tooltips";
import { HoverTooltip } from "@/layers/geojson/tools";
import type { LayerKindName } from "@/layers/utility/createLayerObject";
import {
  type MapboxLayerGroup,
  MapboxSourceGroup,
  addLayerGroup,
  addSource,
  moveZIndex,
  removeLayerGroup,
  removeSource,
  updateMapboxLayoutProperties,
  updateMapboxPaintProperties,
  updateSourceData,
} from "@/layers/utility/mapbox-utils";
import { getDetailsCore } from "@/overlay/components/CommonParts/Threshold/types";
import type { IconData } from "@/reducer/client-models";
import type { PoiLayer } from "@/reducer/client-models/PoiLayer";
import { SENTRY_TRANSACTION_NAMES } from "@/sentry/TransactionConst";
import { captureTransaction } from "@/sentry/helpers";
import { dataProcessingThreadPool } from "@/threads/DataProcessingThread/DataProcessingThreadPool";
import { roundToNearestCacheableTime } from "@/time/roundByLayerKind";
import { safeFmt } from "@/utility/safeFmt";
import { getModelBoundingBox } from "@/weather-parameters/lookup";
import {
  CoordinateSystem,
  type GeoJSONFeatureCollection,
  type PointRequest,
  SinglePointCoordinate,
} from "@mm/api.meteomatics.com";
import type { GuiTimeZone } from "@mm/metx-workbench.meteomatics.com";
import { type DateTime, Duration } from "luxon";
import {
  iconLayerPaintProperties,
  poiMapboxLayerGroup,
  valueLayerLayoutProperties,
  valueLayerOffset,
  valueLayerPaintProperties,
} from "./PoiLayerUtilities";

export class PoiLayerImpl extends LayerBase<PoiLayer> {
  // Flag indicating layer redraw is necessary. Initially layer is dirty, as we wan't to do initial render
  private dirty = true;
  private tooltip: HoverTooltip;
  private advancedThresholdDetailsChange: ChangeDetector<string> = new ChangeDetector<string>("");
  private itemData: ChangeDetector<IconData> = new ChangeDetector<IconData>();

  private mapboxLayerGroup: MapboxLayerGroup<"icon-layer" | "value-layer">;
  private mapboxSrcGroup: MapboxSourceGroup<"poi-data-source">;

  private displayDateTime: ChangeDetector<string>;

  constructor(id: number, props: PoiLayer, scene: ScenePublicApi, timezone: GuiTimeZone) {
    super(id, props, scene, timezone);

    loadTextBackgroundImage(this.scene.getMapboxMap());

    const mapboxLayerGroupId = this.humanReadableId();

    // We first initialize with an empty source, then later set the data after API request.
    this.mapboxSrcGroup = new MapboxSourceGroup(mapboxLayerGroupId, ["poi-data-source"]);
    addSource(this.scene.getMapboxMap(), [
      { sourceId: this.mapboxSrcGroup.id("poi-data-source"), source: emptyGeoJsonSource() },
    ]);

    this.mapboxLayerGroup = poiMapboxLayerGroup(mapboxLayerGroupId, props, this.mapboxSrcGroup.id("poi-data-source"));
    addLayerGroup(this.scene.getMapboxMap(), this.mapboxLayerGroup.initialSpecs());

    this.tooltip = new HoverTooltip(scene.getMapboxMap(), this.mapboxLayerGroup.allIds(), handleRenderTooltip);

    updateMapboxLayoutProperties(this.scene.getMapboxMap(), this.mapboxLayerGroup.id("value-layer"), {
      visibility: this.props.poiOptions.hideLabels ? "none" : "visible",
    });

    // Setup simple change detector, for toggling dirt flag when datetime changes
    this.displayDateTime = new ChangeDetector(
      roundToNearestCacheableTime(this.scene.getDisplayTimeWithOffset(), this.props.kind as LayerKindName).toISO(),
    );
  }

  humanReadableId(): string {
    return safeFmt`metx.PoiLayer#${this.uid}`;
  }

  moveZIndex(beforeLayerId?: string | undefined): void {
    moveZIndex(this.scene.getMapboxMap(), this.mapboxLayerGroup.allIds(), beforeLayerId);
  }

  checker(): PropsChecker<PoiLayer, LayerBase<PoiLayer>> {
    type CheckerProps = CheckedProps<PoiLayer>;
    const checkLayerProp = (property: CheckerProps) => (prev: any, curr: any) => {
      const changed = prev[property] !== curr[property];
      if (changed) {
        this.setLayerProps({ [property]: curr[property] });
        const advancedThresholds = this.props.poiOptions.coloringOptions?.advancedThresholds || [];
        switch (property) {
          case "model":
            this.dirty = true;
            // Avoid transient requests with partially updated props when model changes together
            // with parameter/ensemble selection in the same state update.
            if (prev.parameter_unit === curr.parameter_unit && prev.ens_select === curr.ens_select) {
              this.beforeRender();
            }
            break;
          case "calibrated":
            this.dirty = true;
            this.beforeRender();
            break;
          case "legend_visible":
            break;
          case "parameter_unit":
            updateMapboxLayoutProperties(
              this.scene.getMapboxMap(),
              this.mapboxLayerGroup.id("value-layer"), // Only update the sub layer for the value label
              valueLayerLayoutProperties(this.props) ?? {},
            );
            this.dirty = true;
            // If ens_select is updated in the same change-set, let the ens_select branch trigger fetch.
            if (prev.ens_select === curr.ens_select) {
              this.beforeRender();
            }
            break;
          case "ens_select":
            this.dirty = true;
            this.beforeRender();
            break;
          case "poiOptions": {
            updateMapboxLayoutProperties(
              this.scene.getMapboxMap(),
              this.mapboxLayerGroup.id("value-layer"),
              // Handle offset of the value pills
              valueLayerOffset(this.props) ?? {},
            );
            updateMapboxPaintProperties(
              this.scene.getMapboxMap(),
              this.mapboxLayerGroup.id("value-layer"),
              valueLayerPaintProperties(this.props) ?? {},
            );
            if (this.props.poiOptions.item.icon) {
              updateMapboxPaintProperties(
                this.scene.getMapboxMap(),
                this.mapboxLayerGroup.id("icon-layer"),
                iconLayerPaintProperties(this.props) ?? {},
              );
            }
            updateMapboxLayoutProperties(this.scene.getMapboxMap(), this.mapboxLayerGroup.id("value-layer"), {
              visibility: this.props.poiOptions.hideLabels || !curr.show ? "none" : "visible",
            });
            const detailsChanged = this.advancedThresholdDetailsChange.changed(
              JSON.stringify(advancedThresholds.flatMap((threshold) => getDetailsCore(threshold.details))),
            );
            this.dirty = detailsChanged;
            if (curr.show) {
              this.beforeRender();
            }
            break;
          }
          case "show": {
            // show/hide layers that are actually visible.
            const modifiedLayerGroups = [
              ...(!this.props.poiOptions.hideLabels ? this.mapboxLayerGroup.id("value-layer") : []),
              ...(this.props.poiOptions.item.icon ? this.mapboxLayerGroup.id("icon-layer") : []),
            ];
            updateMapboxLayoutProperties(this.scene.getMapboxMap(), modifiedLayerGroups, {
              visibility: curr.show ? "visible" : "none",
            });
            break;
          }
          case "opacity":
            updateMapboxPaintProperties(
              this.scene.getMapboxMap(),
              this.mapboxLayerGroup.id("value-layer"),
              valueLayerPaintProperties(this.props) ?? {},
            );
            updateMapboxPaintProperties(
              this.scene.getMapboxMap(),
              this.mapboxLayerGroup.id("icon-layer"),
              iconLayerPaintProperties(this.props) ?? {},
            );
            break;
          case "custom_options":
            break;
          default: {
            const _exhaustive: never = property;
            return _exhaustive;
          }
        }
      }
      return changed;
    };
    return {
      model: checkLayerProp("model"),
      calibrated: checkLayerProp("calibrated"),
      parameter_unit: checkLayerProp("parameter_unit"),
      poiOptions: checkLayerProp("poiOptions"),
      legend_visible: checkLayerProp("legend_visible"),
      opacity: checkLayerProp("opacity"),
      show: checkLayerProp("show"),
      ens_select: checkLayerProp("ens_select"),
      custom_options: checkLayerProp("custom_options"),
    };
  }

  get mapboxIndex(): string {
    return this.mapboxLayerGroup.mainId();
  }

  removeLayer(): void {
    this.tooltip.dispose();
    removeLayerGroup(this.scene.getMapboxMap(), this.mapboxLayerGroup.allIds());
    removeSource(this.scene.getMapboxMap(), this.mapboxSrcGroup.allIds());
  }

  beforeRender(): void {
    // If display datetime has changed, redraw is necessary
    if (
      this.displayDateTime.changed(
        roundToNearestCacheableTime(this.scene.getDisplayTimeWithOffset(), this.props.kind as LayerKindName).toISO(),
      )
    ) {
      this.dirty = true;
    }

    // If layer is dirty, trigger redraw and reset flag to indicate, that it's clean
    this.updateTrigger();
    this.dirty = false;
  }

  isRebuffering(time: DateTime): boolean {
    const dateTimeWithOffset = roundToNearestCacheableTime(
      this.scene.getDateTimeWithOffset(time),
      this.props.kind as LayerKindName,
    );
    const { request } = this.createRequestContext(dateTimeWithOffset);
    const { synchronous } = this.peekLayerCache(request);
    return synchronous === SynchrounousState.StillPending;
  }

  updateTrigger(): void {
    const itemDataChanged = this.itemData.changed(this.props.poiOptions.item);
    const hasParameter = !!this.props.parameter_unit?.trim();

    // POIs without parameter_unit should only render static icon/text data.
    // Requesting parameter data in this case resolves to empty GeoJSON and can overwrite source data.
    if (!hasParameter) {
      if (this.dirty || itemDataChanged) {
        this.updateRawItemData(this.props.poiOptions.item);
      }
      return;
    }

    if (this.dirty) {
      this.requestAllPointsData(this.dirty);
    } else if (itemDataChanged) {
      this.requestAllPointsData(false);
    }
  }
  private updateRawItemData(item: IconData) {
    dataProcessingThreadPool
      .createPOIGeoJson(item)
      .then((geoJsonFeature) => {
        updateSourceData(this.scene.getMapboxMap(), this.mapboxSrcGroup.id("poi-data-source"), geoJsonFeature);
      })
      .catch((e) => {
        console.error(e);
      });
  }

  /**
   * Builds the Meteomatics point request from the already-filtered coordinate subset for the active model.
   */
  private createRequest(
    dateTimeWithOffset: DateTime,
    coordsList: IconData["coords"],
  ): PointRequest<CoordinateSystem.WGS84> {
    return {
      coordinates: coordsList.map(
        (coord) => new SinglePointCoordinate<CoordinateSystem.WGS84>(coord.lat, coord.lon, CoordinateSystem.WGS84),
      ),
      startDatetime: dateTimeWithOffset,
      duration: Duration.fromISO("PT1H"),
      temporalResolution: Duration.fromISO("PT1H"),
      height: 1,
      parameters: [this.props.parameter_unit],
      model: this.props.model,
      ensSelect: this.props.ens_select ? this.props.ens_select : "",
      requestOptions: {
        calibrated: !!this.props.calibrated,
      },
      apiUrlSuffix: this.props.poiOptions.apiUrlSuffix,
      boundingBoxLimit: getModelBoundingBox(this.props.model),
      width: 1,
    };
  }

  /**
   * Computes the model-boundary partition once and returns both the filtered request and the original indexes
   * needed later to merge API values back onto the full PoI list.
   */
  private createRequestContext(dateTimeWithOffset: DateTime) {
    const partition = partitionPoiCoordsByBoundary(
      this.props.poiOptions.item.coords,
      getModelBoundingBox(this.props.model),
    );
    return {
      partition,
      request: this.createRequest(dateTimeWithOffset, partition.inBoundsCoords),
    };
  }

  /**
   * Requests point data only for in-bounds coordinates while still updating the source with the full rendered
   * GeoJSON returned by `PoiCache`, including fallback markers for points outside the model boundary.
   */
  requestAllPointsData(refresh: boolean) {
    // async api request call
    const dateTimeWithOffset = roundToNearestCacheableTime(
      this.scene.getDisplayTimeWithOffset(),
      this.props.kind as LayerKindName,
    );
    const { partition, request } = this.createRequestContext(dateTimeWithOffset);
    const result = networkCaches.poi_cache.retrievePoiByParameter(
      request,
      this.props.poiOptions.item,
      partition.inBoundsIndexes,
      PoiParameterRequestKind.SingleParameterColoring,
      refresh,
      this.props.poiOptions.coloringOptions?.advancedThresholds,
    );
    // TODO: This line starts triggering the infinite loop, potential problem may be in Scene/Compositor.
    // The step (3) below is done in Scene/Compositor. And this is a problem for all layer implementation classes.
    // How it goes:
    // 1. After the promise has resolved, the mapbox triggers beforeRender callback
    // 2. Our layer implementation class has also `beforeRender` method.
    // 3. This `beforeRender()` is bind() to mapbox's lifecycle, which gets triggered on source update etc.
    // 4. Since `layerImpl.beforeRender()` triggers the source update, we have a inifinite loop logic :)
    // scheduleSourceUpdate(this.scene.getMapboxMap(), this.mapboxSrcGroup[0], result.asynchronous);
    result.asynchronous
      .then((geoJSON) => {
        updateSourceData(this.scene.getMapboxMap(), this.mapboxSrcGroup.id("poi-data-source"), geoJSON);
      })
      .catch((e) => {
        console.error(e);
      });
  }

  /**
   * Prefetches the same filtered request path used during rendering so time navigation warms the boundary-aware
   * PoI cache instead of issuing requests for points the model cannot serve.
   */
  fetchData(timeFrame: DateTime): VoidAsyncResult {
    if (!this.props.parameter_unit?.trim()) {
      return createResolvedAsyncResult(undefined);
    }

    const dateTimeWithOffset = roundToNearestCacheableTime(
      this.scene.getDateTimeWithOffset(timeFrame),
      this.props.kind as LayerKindName,
    );
    const { partition, request } = this.createRequestContext(dateTimeWithOffset);
    const tags = {
      layerKind: `${this.props.kind ? this.props.kind : "undefined"}`,
      parameter: `${this.props.parameter_unit}`,
      requestedDate: `${dateTimeWithOffset}`,
    };

    const result = networkCaches.poi_cache.retrievePoiByParameter(
      request,
      this.props.poiOptions.item,
      partition.inBoundsIndexes,
      PoiParameterRequestKind.SingleParameterColoring,
      undefined,
      this.props.poiOptions.coloringOptions?.advancedThresholds,
    );

    if (!this.scene.isAnimation()) {
      return asVoidAsyncResult(
        result,
        captureTransaction(result.asynchronous, SENTRY_TRANSACTION_NAMES.POI_LAYER_LOADING_SPEED, tags),
      );
    }

    return asVoidAsyncResult(result);
  }

  getActiveWeatherParametersAsString() {
    return [
      {
        model: this.props.model,
        parameter: this.props.parameter_unit,
      },
    ];
  }

  peekLayerCache(request: PointRequest<CoordinateSystem.WGS84>): AsyncResult<GeoJSONFeatureCollection> {
    // if there is no parameter or no items we don't need to request anything.
    if (request.parameters[0] === "" || this.props.poiOptions.item.coords.length === 0) {
      return createResolvedAsyncResult(emptyFeatureCollection);
    }
    const cacheId = generatePOILayerCacheId(
      request,
      PoiParameterRequestKind.SingleParameterColoring,
      this.props.poiOptions.item,
      JSON.stringify(this.props.poiOptions.coloringOptions?.advancedThresholds ?? []),
    );
    return networkCaches.poi_cache.peekCache(cacheId);
  }
}
